// canvasZoomUtils.js

/**
 * Calculates a new position for the canvas so that zooming in/out keeps the same center.
 *
 * @param {object} params
 * @param {number} params.scale - New scale value.
 * @param {number} params.prevScale - Previous scale value.
 * @param {{ x: number, y: number }} params.position - Current canvas position.
 * @param {HTMLDivElement} params.container - Canvas container element.
 * @returns {{ x: number, y: number }} - New position to keep zoom centered.
 */
export const getZoomCenteredPosition = ({ scale, prevScale, position, container }) => {
  if (!container || scale === prevScale) return position;

  const containerRect = container.getBoundingClientRect();
  const centerX = containerRect.width / 2;
  const centerY = containerRect.height / 2;

  const canvasX = (centerX - position.x) / prevScale;
  const canvasY = (centerY - position.y) / prevScale;

  const newX = centerX - canvasX * scale;
  const newY = centerY - canvasY * scale;

  return { x: newX, y: newY };
};

/**
 * Applies centered zooming to a canvas by updating position if scale changes.
 *
 * @param {object} params
 * @param {number} params.scale - New scale.
 * @param {React.MutableRefObject} params.prevScaleRef - Ref storing previous scale.
 * @param {{ x: number, y: number }} params.position - Current position.
 * @param {HTMLDivElement} params.container - Canvas container.
 * @param {(newPos: { x: number, y: number }) => void} params.setPosition - Position setter.
 */
export const applyZoomToCanvas = ({ scale, prevScaleRef, position, container, setPosition }) => {
  const prevScale = prevScaleRef.current;
  if (!container || scale === prevScale) return;

  const newPosition = getZoomCenteredPosition({
    scale,
    prevScale,
    position,
    container,
  });

  setPosition(newPosition);
  prevScaleRef.current = scale;
};
